/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The large select of the hero's filter card, the overview's compare picker
 * and the profile page's compare picker: a trigger and a listbox portaled
 * under it. The listbox itself — roles, open state, every key — is
 * useListbox (src/hooks/useListbox.ts), shared with the comparer's
 * FilterSelect; the markup and the look are this file's.
 *
 * Named by the visible text beside it (`labelledBy`, 2026-10-14): the
 * listbox by that label, the trigger by the label and the selected option
 * ("Contractduur 1 jaar") — the WAI-ARIA APG's collapsible listbox. Until then
 * the listbox had no name and the trigger said only its value.
 */

import React, { useState, useEffect, useId } from 'react';
import { ClientPortal } from './ClientPortal';
import { m, AnimatePresence } from 'motion/react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';
import { useListbox } from '../hooks/useListbox';

interface Option {
  label: string;
  value: string;
}

interface CustomDropdownProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  /**
   * The id of the visible text that says what is chosen here — the field's
   * label, a section title. It names the listbox, and the trigger as
   * "<label> <selected option>".
   */
  labelledBy: string;
  className?: string;
  /**
   * Several options at once (the Stap 1 looptijd, 2026-10-16): a pick is a
   * toggle the host resolves in `onChange`, `isSelected` says which options
   * the value holds, and the closed control says `valueText`.
   */
  multiple?: boolean;
  isSelected?: (optionValue: string) => boolean;
  valueText?: string;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  labelledBy,
  className,
  multiple = false,
  isSelected,
  valueText,
}) => {
  const listbox = useListbox<HTMLUListElement>({ value, onChange, multiple, isSelected });
  const valueId = useId();
  const { open: isOpen, triggerRef } = listbox;
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, triggerRef]);

  return (
    <div className={cn('relative', className)}>
      <button
        type="button"
        {...listbox.triggerProps}
        aria-labelledby={`${labelledBy} ${valueId}`}
        className="w-full flex items-center justify-between bg-slate-700 border-2 border-slate-600 text-white font-black text-sm py-4 px-5 rounded-2xl shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:border-emerald-500 transition-all cursor-pointer hover:border-emerald-500/50"
      >
        <span id={valueId}>{(multiple ? valueText : undefined) ?? selectedOption.label}</span>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-emerald-500 transition-transform duration-300',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      <ClientPortal>
        <AnimatePresence>
          {isOpen && (
            <m.ul
              {...listbox.menuProps}
              {...listbox.listboxProps}
              aria-labelledby={labelledBy}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              onAnimationComplete={listbox.onMenuShown}
              style={{
                position: 'absolute',
                top: dropdownPosition.top + 8,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
              }}
              className="z-[9999] bg-slate-800 border border-slate-700 rounded-2xl shadow-xl overflow-hidden"
            >
              {options.map((option) => {
                const chosen =
                  multiple && isSelected ? isSelected(option.value) : value === option.value;
                return (
                  // `none`: the options are the listbox's own children, not list items in it.
                  <li key={option.value} role="none">
                    <button
                      type="button"
                      {...listbox.optionProps(option.value)}
                      className={cn(
                        'w-full text-left px-5 py-3 text-sm font-medium transition-colors hover:bg-emerald-900/20 hover:text-emerald-400',
                        multiple && 'flex items-center gap-3',
                        chosen ? 'bg-emerald-900/20 text-emerald-400 font-bold' : 'text-slate-300'
                      )}
                    >
                      {multiple && (
                        // A check box, so "chosen" is a shape as well as a colour (WCAG 1.4.1).
                        <span
                          aria-hidden="true"
                          className={cn(
                            'inline-flex size-4 shrink-0 items-center justify-center rounded border',
                            chosen
                              ? 'border-emerald-500 bg-emerald-500 text-slate-950'
                              : 'border-slate-500'
                          )}
                        >
                          {chosen && <Check className="size-3" strokeWidth={3} />}
                        </span>
                      )}
                      {option.label}
                    </button>
                  </li>
                );
              })}
            </m.ul>
          )}
        </AnimatePresence>
      </ClientPortal>
    </div>
  );
};
